import "./LynePeoplePicker.scss";

import { SbbAutocomplete } from "@sbb-esta/lyne-react/autocomplete";
import { SbbLoadingIndicatorCircle } from "@sbb-esta/lyne-react/loading-indicator-circle";
import { SbbOption } from "@sbb-esta/lyne-react/option";
import { useQuery } from "@tanstack/react-query";
import { FormField } from "components/Forms/FormField";
import { toastError } from "helpers";
import { createLogger } from "logging";
import { UserLookupModel } from "models/UserLookupModel";
import { useApi } from "providers";
import { useTranslations } from "providers";
import { useRef, useState } from "react";

import { defaultSize } from "./LyneConstants";
import { LyneFieldError } from "./LyneFieldError";
import { LyneFormField } from "./LyneFormField";
import { LyneInputReset } from "./LyneInputReset";
import { LynePeopleOption } from "./LynePeopleOption";
import { LynePickerInput, LynePickerInputHandle } from "./LynePickerInput";

const logger = createLogger("LynePeoplePicker");

interface LynePeoplePickerProps extends FormField {
  readOnly?: boolean;
  autoFocus?: boolean;
  currentUser?: UserLookupModel;
  showSelectedUser?: boolean;
  onChange: (user: UserLookupModel | undefined, e: unknown) => void;
  size?: "s" | "m" | "l";
  error?: string;
}

export const LynePeoplePicker: React.FC<LynePeoplePickerProps> = ({
  name,
  autoFocus = false,
  readOnly = false,
  disabled = false,
  currentUser,
  onChange,
  size = defaultSize,
  required,
  label,
  contextHelp,
  error,
}) => {
  const i18n = useTranslations();
  const pickerResetRef = useRef<LynePickerInputHandle>(null);
  const { userApi } = useApi();

  const [searchValue, setSearchValue] = useState("");

  // Resolve display name from API when currentUser is set but lacks displayName
  const resolveKey = currentUser?.upn ?? currentUser?.personnelNumber ?? String(currentUser?.id ?? "");
  const resolveQueryResult = useQuery({
    queryKey: [`Users|Resolve|${resolveKey}`],
    queryFn: async () => {
      const data = await userApi.searchUsers(resolveKey);
      const result = data?.[0] ?? null;
      logger.trace("Loaded", data, result);
      return result;
    },
    enabled: !!currentUser && !currentUser.displayName,
  });
  const resolvedUser = resolveQueryResult.data;
  const resolveIsLoading = resolveQueryResult.isFetching;

  // externalValue drives both "show selected user's name" and "clear on reset".
  const externalValue = currentUser?.displayName ?? resolvedUser?.displayName ?? "";

  const searchQueryResult = useQuery({
    queryKey: [`Users|Search|${searchValue}`],
    queryFn: async () => {
      let result: UserLookupModel[] = [];
      let data;
      if (searchValue) {
        try {
          data = await userApi.searchUsers(searchValue);
          result = data ?? [];
        } catch (error) {
          toastError("Failed to search for users", error);
        }
      }

      logger.trace("Loaded", data, result);
      return result;
    },
    enabled: !!searchValue,
  });
  const users = searchQueryResult.data || [];
  const searchIsLoading = searchQueryResult.isFetching;

  const isLoading = resolveIsLoading || searchIsLoading;
  const handleReset = () => {
    logger.debug(`handleReset(): `);
    setSearchValue("");
    if (currentUser) onChange(undefined, null);
    pickerResetRef.current?.reset();
  };

  return (
    <LyneFormField
      size={size}
      className="lyne-picker people-picker"
      name={name}
      required={required}
      label={label}
      contextHelp={contextHelp}
    >
      <LynePickerInput
        logger={logger}
        resetRef={pickerResetRef}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        placeholder={i18n.UserPicker_Placeholder}
        externalValue={externalValue}
        onDebouncedChange={setSearchValue}
      />

      {isLoading && <SbbLoadingIndicatorCircle color="smoke" />}

      <LyneInputReset onClick={handleReset} disabled={(searchValue.length === 0 && !currentUser) || disabled} />

      {users.length > 0 && (
        <SbbAutocomplete>
          {users.map((option) => {
            const optionValue = option.personnelNumber;
            const key = `${name}-${optionValue}`;

            return (
              <SbbOption
                key={key}
                value={optionValue}
                onOptionselected={(e) => {
                  e.preventDefault();
                  handleReset();
                  onChange(option, e);
                }}
              >
                <LynePeopleOption user={option} />
              </SbbOption>
            );
          })}
        </SbbAutocomplete>
      )}
      <LyneFieldError error={error} />
    </LyneFormField>
  );
};
